import { DISCIPLINE_SORT_KEYS, type DisciplineItem, type DisciplineSortKey, type SportSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, Icons, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { EmptyCard } from "../../components/common/empty-card";
import { timestampColumn } from "../../components/common/list-columns";
import { StatusTag } from "../../components/competition/list-chips";
import { type DisciplineFormTarget, DisciplineFormModal } from "../../components/sports/discipline-form-modal";
import { canSetUpCompetition, sportsListSearch } from "../../components/sports/shared";
import { usePageBar } from "../../components/shell/page-bar";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { requireAdminRole } from "../../lib/auth/guards";
import { gamesOptionsOptions } from "../../lib/competition/games";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteDiscipline, disciplineStatsOptions, disciplinesListOptions, invalidateDisciplines, sportDetailOptions } from "../../lib/sports/queries";

/**
 * Settings › Sports › one sport's disciplines. The sport is the URL param.
 * `list` is the sports list's own view (games, search, sort, page), carried
 * here so the back link returns to it.
 */
export const Route = createFileRoute("/_app/settings/sports/$sportId")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: listSearch(DISCIPLINE_SORT_KEYS, {
		list: sportsListSearch,
		// A boolean, as on the sports list: the router JSON-parses `?is_active=false`.
		is_active: z.union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")]),
	}),
	component: DisciplinesPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface DisciplineFilters extends Record<string, unknown> {
	search: string;
	is_active: "" | "true" | "false";
}
const EMPTY_FILTERS: DisciplineFilters = { search: "", is_active: "" };

function DisciplinesPage() {
	const { t } = useTranslation("sports");
	const { t: tc } = useTranslation("common");
	const { session } = Route.useRouteContext();
	const { sportId } = Route.useParams();
	const search = Route.useSearch();
	const { formatNumber } = useUiStrings();
	const canWrite = canSetUpCompetition(session.active_role);
	const [form, setForm] = useState<DisciplineFormTarget | null>(null);

	const detail = useQuery(sportDetailOptions(sportId));
	const games = useQuery(gamesOptionsOptions());
	// The sport's discipline counts — one aggregate statement, not narrowed by search or status.
	const stats = useQuery(disciplineStatsOptions(sportId));
	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => disciplinesListOptions({ ...q, sport_id: sportId, is_active: search.is_active === undefined ? undefined : search.is_active ? "true" : "false" }),
		columnsKey: "disciplines",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): DisciplineFilters => ({ search: s.search ?? "", is_active: s.is_active === undefined ? "" : s.is_active ? "true" : "false" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, is_active: f.is_active === "" ? undefined : f.is_active === "true" }),
		},
		summary: { range: (v) => t("pagination.disciplineSummary", v), empty: t("pagination.disciplineEmpty") },
	});
	const del = useConfirmDelete<DisciplineItem>({
		remove: async (r) => {
			try {
				await deleteDiscipline(r.discipline_id, `Deleted discipline ${r.sport_code}/${r.code}`);
			} catch (err) {
				// 409 = it still has events: say so in our words.
				if (err instanceof ApiError && err.code === "CONFLICT") throw new Error(t("errors.disciplineInUse"));
				throw err;
			}
		},
		invalidate: invalidateDisciplines,
		deleted: (r) => t("disciplineDelete.deleted", { name: r.name }),
	});
	const sport = detail.data;
	// Back to the list as it was left; straight from a link elsewhere, to this sport's games.
	const backSearch = search.list ?? (sport ? { games_id: sport.games_id } : {});

	// The top bar carries the sport (code, name, inactive) and the way back to the list it came from.
	usePageBar({
		title: sport ? (
			<span className="inline-flex min-w-0 items-center gap-[0.5rem]">
				<ListTag className="font-mono tracking-[0.1em]">{sport.code}</ListTag>
				<span className="truncate">{sport.name}</span>
				{!sport.is_active && <ListTag className="bg-gms-soft-2 text-gms-ink-3">{t("detail.inactive")}</ListTag>}
			</span>
		) : undefined,
		subtitle: t("detail.subtitle"),
		back: { to: "/settings/sports", search: backSearch, label: t("detail.backToSports") },
	});
	const total = page.list.data?.total;
	// Sport keys: a child route's search is merged with the sports list's, whose `sort` lacks `sport` (meaningless inside one sport anyway).
	const sort = sortSelect<SportSortKey>(
		{ ...search, sort: search.sort === "sport" ? undefined : search.sort },
		"sort_order:asc",
		[
			{ value: "sort_order:asc", label: t("sort.programme") },
			{ value: "sort_order:desc", label: t("sort.programmeDesc") },
			{ value: "code:asc", label: tc("sort.codeAsc") },
			{ value: "code:desc", label: tc("sort.codeDesc") },
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "created_at:desc", label: tc("sort.newest") },
			{ value: "created_at:asc", label: tc("sort.oldest") },
		],
		page.patch,
	);

	const columns: ColumnDef<DisciplineItem, DisciplineSortKey>[] = [
		{ key: "name", header: t("disciplineTable.name"), sortable: true, lockVisible: true, minWidth: "14rem", cell: (r) => <PrimaryCell sub={r.code}>{r.name}</PrimaryCell> },
		{ key: "sort_order", header: t("disciplineTable.sortOrder"), sortable: true, width: "5.5rem", numeric: true, cell: (r) => <span className="tabular-nums">{r.sort_order}</span> },
		{
			key: "is_active" as DisciplineSortKey,
			header: t("disciplineTable.status"),
			width: "7rem",
			cell: (r) => <StatusTag active={r.is_active} activeLabel={t("disciplineTable.active")} inactiveLabel={t("disciplineTable.inactive")} />,
		},
		{
			key: "events_count" as DisciplineSortKey,
			header: t("disciplineTable.events"),
			width: "6.5rem",
			numeric: true,
			cell: (r) =>
				r.events_count > 0 ? (
					<Link
						to="/settings/sports"
						search={{ tab: "events", games_id: r.games_id, sport_id: r.sport_id, discipline_id: r.discipline_id }}
						className="font-semibold tabular-nums text-gms-navy hover:underline"
						title={t("disciplineTable.eventsTitle")}
					>
						{formatNumber(r.events_count)}
					</Link>
				) : (
					<span className="text-base-content/30">{formatNumber(0)}</span>
				),
		},
		timestampColumn("created_at", t("disciplineTable.created"), true),
	];

	if (detail.isPending) {
		return (
			<div className="flex flex-col items-center gap-[0.75rem] py-[4rem]">
				<span className="loading loading-spinner loading-md text-gms-ink-3" />
				<p className="text-[0.8125rem] text-gms-ink-3">{tc("state.loading")}</p>
			</div>
		);
	}

	if (detail.isError || sport === undefined) {
		return (
			<EmptyCard
				tone="bad"
				icon={<Icons.Alert />}
				title={t("detail.loadFailedTitle")}
				actions={
					<>
						<button type="button" className="btn btn-line btn-sm" onClick={() => void detail.refetch()}>
							{tc("action.retry")}
						</button>
						<Link to="/settings/sports" search={backSearch} className="btn btn-line btn-sm">
							{t("detail.backToSports")}
						</Link>
					</>
				}
			>
				{detail.isError ? errorMessage(detail.error) : undefined}
			</EmptyCard>
		);
	}

	// Games names are proper nouns and stay as stored; the code until the options arrive.
	const gamesName = games.data?.find((g) => g.games_id === sport.games_id)?.name ?? sport.games_code;

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={sport.name}
				count={total === undefined ? undefined : t("detail.count", { count: total, n: formatNumber(total) })}
				description={t("detail.meta", { games: gamesName, order: sport.sort_order })}
				actionLabel={t("detail.addDiscipline")}
				onAction={canWrite ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.disciplines"), value: total ?? "—", note: page.hasFilters ? t("stats.matching") : undefined },
					...(stats.data
						? [
								{ key: "active", label: t("stats.activeDisciplines"), value: stats.data.active },
								{ key: "events", label: t("stats.events"), value: stats.data.events_total },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.disciplineSearchPlaceholder")}
				sort={sort}
				chips={[
					{
						kind: "options",
						key: "status",
						label: t("filters.statusLabel"),
						options: [
							{ value: "true", label: t("filters.active"), count: stats.data?.active },
							{ value: "false", label: t("filters.inactive"), count: stats.data?.inactive },
						],
						value: search.is_active === undefined ? [] : [search.is_active ? "true" : "false"],
						onChange: (v) => page.patch({ is_active: v[0] === undefined ? undefined : v[0] === "true" }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<DisciplineItem, DisciplineSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.discipline_id}
				empty={page.emptyState({
					filtered: { title: t("empty.disciplineFilteredTitle"), description: t("empty.disciplineFilteredDescription") },
					none: { title: t("empty.disciplineNoneTitle"), description: t("empty.disciplineNoneDescription", { name: sport.name }) },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					canWrite
						? (r) => (
								<EditDeleteActions
									editLabel={t("disciplineTable.editLabel", { name: r.name })}
									onEdit={() => setForm({ mode: "edit", discipline: r })}
									deleteLabel={t("disciplineTable.deleteLabel", { name: r.name })}
									onDelete={() => del.ask(r)}
									canDelete={r.events_count === 0}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{canWrite && <DisciplineFormModal target={form} sport={sport} onClose={() => setForm(null)} />}
			{canWrite && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("disciplineDelete.title")}
					description={<Trans t={t} i18nKey="disciplineDelete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("disciplineDelete.confirm")}
					errorPrefix={t("disciplineDelete.failed")}
				/>
			)}
		</div>
	);
}
